/**
 * iter 72ak — Statement of Account dialog (works for both AR customers and
 * AP vendors). Renders Date range pickers + Download PDF + Email actions.
 */
import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { FileText, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format, startOfYear } from "date-fns";
import { fmtISO, downloadStream } from "./_shared";

/**
 * @param {"customer"|"vendor"} kind
 * @param {{id, name, email}} party
 */
export function StatementDialog({ open, onOpenChange, kind, party }) {
  const [start, setStart] = useState(format(startOfYear(new Date()), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [email, setEmail] = useState(party?.email || "");
  const [sending, setSending] = useState(false);

  if (!party) return null;
  const basePath = kind === "customer" ? "customers" : "vendors";
  const fileStem = kind === "customer" ? "statement" : "vendor_statement";
  const heading = kind === "customer" ? "Statement of Account" : "Vendor Statement";

  const download = async () => {
    try {
      await downloadStream(
        `/${basePath}/${party.id}/statement.pdf`,
        { start, end },
        `${fileStem}_${(party.name || party.id).replace(/\s+/g, "_")}.pdf`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't download statement");
    }
  };

  const sendEmail = async () => {
    if (!email) {
      toast.error("Please enter a recipient email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/${basePath}/${party.id}/statement/email`,
                                        { email, start, end });
      if (data.delivery_mode === "queued_no_key") {
        toast.success(`Statement queued (Resend key not configured — recipient: ${data.recipient})`);
      } else {
        toast.success(`Statement emailed to ${data.recipient}`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send statement");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${kind}-statement-dialog`} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>{party.name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                    data-testid={`${kind}-stmt-start`} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                    data-testid={`${kind}-stmt-end`} />
          </div>
          <div className="col-span-2">
            <Label>Recipient email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={kind === "customer" ? "customer@example.com" : "vendor@example.com"}
                    data-testid={`${kind}-stmt-email`} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={download} data-testid={`${kind}-stmt-download`}>
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <Button onClick={sendEmail} disabled={sending} className="btn-brand"
                   data-testid={`${kind}-stmt-send`}>
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
